import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useInvoiceApi } from '../../../hooks/useInvoiceApi';
import { showAlert } from '../../../utils/sweetAlertUtils';
import { InvoiceTypeConstants } from '../../../constants/InvoiceTypeConstants';

const FinanceExport: React.FC = () => {
    const { type } = useParams<{ type: string }>();
    const { extract, isLoading } = useInvoiceApi();

    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [dateField, setDateField] = useState<'PaidDate' | 'CreatedUtc'>('PaidDate');

    const handleClear = () => {
        setFromDate('');
        setToDate('');
        setDateField('PaidDate');
    };

    // Determine page title and invoice type based on URL parameter
    const isRenewals = type === 'renewals';
    const pageTitle = isRenewals ? 'Renewal Export' : 'Invoice Export';
    const invoiceTypeIds = isRenewals
        ? [
            InvoiceTypeConstants.INVOICE_RENEWAL,
            InvoiceTypeConstants.SALES_ORDER_RENEWAL
        ]
        : [
            InvoiceTypeConstants.GENERAL_ONCE_OFF,
            InvoiceTypeConstants.INVOICE_ONCE_OFF,
            InvoiceTypeConstants.SALES_ORDER_ONCE_OFF
        ];

    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Reports', href: '#' },
        { label: 'Finance', href: '#' },
        { label: pageTitle, href: '#' },
    ];

    const handleExport = async () => {
        // Validate that both dates are selected
        if (!fromDate || !toDate) {
            showAlert({
                title: 'Validation Error',
                text: 'Please select both From Date and To Date',
                icon: 'error',
            });
            return;
        }

        // Validate that fromDate is not after toDate
        if (new Date(fromDate) > new Date(toDate)) {
            showAlert({
                title: 'Validation Error',
                text: 'From Date cannot be after To Date',
                icon: 'error',
            });
            return;
        }

        try {
            // Build search filters with InvoiceTypeIds and date range
            const filters: any = {
                InvoiceTypeIds: invoiceTypeIds,
            };

            // Add date range filters based on selected field
            if (dateField === 'PaidDate') {
                filters.FromPaidDate = fromDate;
                filters.ToPaidDate = toDate;
            } else {
                filters.FromCreatedUtc = fromDate;
                filters.ToCreatedUtc = toDate;
            }

            const response = await extract({
                filters,
                page: 1,
                pageSize: 9999999, // Export all matching records
                sortBy: 'CreatedUtc',
                sortDescending: true,
            });

            if (response.data) {
                // Open the SAS URL in a new window to download the Excel file
                window.open(response.data, '_blank');
                showAlert({
                    title: 'Export Successful',
                    text: 'Your export has been generated and will download shortly.',
                    icon: 'success',
                });
            }
        } catch (error) {
            console.error('Error exporting invoices:', error);
            showAlert({
                title: 'Export Failed',
                text: 'An error occurred while generating the export. Please try again.',
                icon: 'error',
            });
        }
    };

    return (
        <AdminLayout
            activeMenuItem="/admin/reports"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Admin']}
            requiredPermission="Reports.Finance.View"
        >
            <Card
                title={pageTitle}
                subtitle={`Export ${isRenewals ? 'renewal' : 'invoice'} records based on date range filters`}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                        id="fromDate"
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="Select From Date"
                        required={true}
                    />
                    <FormField
                        id="toDate"
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="Select To Date"
                        required={true}
                    />
                    <FormField
                        id="dateField"
                        label="Date Field to Filter"
                        type="dropdown"
                        value={dateField}
                        onChange={(e) => setDateField(e.target.value as 'PaidDate' | 'CreatedUtc')}
                        options={[
                            {
                                value: 'CreatedUtc',
                                label: 'Created Date',
                                description: 'Filter by when invoice was created in the system'
                            },
                            {
                                value: 'PaidDate',
                                label: 'Paid Date',
                                description: 'Filter by when invoice was marked as paid'
                            }
                        ]}
                        placeholder="Select Date Field"
                        required={true}
                    />
                </div>
                <div className="mt-6 flex gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClear}
                        disabled={isLoading}
                    >
                        Clear
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleExport}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Generating...' : 'Export'}
                    </Button>
                </div>
            </Card>
        </AdminLayout>
    );
};

export default FinanceExport;
